import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDiet, BodyMeasures } from "@/contexts/DietContext";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { savePendingMeasures } from "@/lib/pendingMeasures";
import { toast } from "sonner";
import { calcMacroTargets } from "@/lib/macroSolver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Activity, Target, Flame, Clock, Dumbbell, Scale, Ruler, CalendarDays } from "lucide-react";

const goals = [
  { value: "lose" as const, label: "Emagrecer", emoji: "🔥" },
  { value: "maintain" as const, label: "Manter Peso", emoji: "🏋️" },
  { value: "gain" as const, label: "Ganhar Massa", emoji: "💪" },
];

const activityLevels = [
  { value: "sedentary" as const, label: "Sedentário", desc: "Pouco ou nenhum exercício" },
  { value: "light" as const, label: "Leve", desc: "Exercício 1-3 dias/semana" },
  { value: "moderate" as const, label: "Moderado", desc: "Exercício 3-5 dias/semana" },
  { value: "active" as const, label: "Ativo", desc: "Exercício 6-7 dias/semana" },
  { value: "very_active" as const, label: "Muito Ativo", desc: "Exercício intenso diário" },
];

const defaultTimes = ["07:00", "09:30", "12:00", "15:00", "19:00"];

/**
 * Calorie calculation based on the Mifflin-St Jeor equation (1990),
 * recommended by the Academy of Nutrition and Dietetics as the most
 * accurate BMR formula for the general population.
 *
 * Activity multipliers follow the Katch-McArdle standard.
 *
 * Goal adjustments use percentage-based approach (more accurate than
 * fixed kcal offsets for different body sizes):
 *   - Lose: −20% TDEE (moderate deficit, ~0.5-1kg/week)
 *   - Gain: +15% TDEE (lean bulk, ~0.25-0.5kg/week)
 *
 * References:
 *   Mifflin MD et al. Am J Clin Nutr. 1990;51(2):241-247.
 *   Frankenfield D et al. J Am Diet Assoc. 2005;105(5):775-789.
 */
const calcCalories = (w: number, h: number, age: number, sex: string, goal: string, activity: string) => {
  // Mifflin-St Jeor BMR
  const bmr = sex === "male"
    ? 10 * w + 6.25 * h - 5 * age + 5
    : 10 * w + 6.25 * h - 5 * age - 161;

  const multipliers: Record<string, number> = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
  };

  const tdee = bmr * (multipliers[activity] || 1.55);

  // Percentage-based adjustments for goals
  if (goal === "lose") return Math.round(tdee * 0.80);  // −20%
  if (goal === "gain") return Math.round(tdee * 1.15);  // +15%
  return Math.round(tdee);
};

const Dashboard = () => {
  const { measures, setMeasures, selectedPlan } = useDiet();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [weight, setWeight] = useState(measures?.weight?.toString() || "");
  const [height, setHeight] = useState(measures?.height?.toString() || "");
  const [age, setAge] = useState(measures?.age?.toString() || "");
  const [sex, setSex] = useState<"male" | "female">(measures?.sex || "male");
  const [goal, setGoal] = useState<"lose" | "maintain" | "gain">(measures?.goal || "lose");
  const [activity, setActivity] = useState(measures?.activityLevel || "moderate");
  const [calorieMode, setCalorieMode] = useState<"auto" | "manual">("auto");
  const [manualCalories, setManualCalories] = useState("");
  const [mealTimes, setMealTimes] = useState<string[]>(measures?.mealTimes || defaultTimes);

  useEffect(() => {
    if (!measures) {
      setWeight("");
      setHeight("");
      setAge("");
      setSex("male");
      setGoal("lose");
      setActivity("moderate");
      setMealTimes(defaultTimes);
      setManualCalories("");
      setCalorieMode("auto");
      return;
    }
    setWeight(measures.weight?.toString() || "");
    setHeight(measures.height?.toString() || "");
    setAge(measures.age?.toString() || "");
    setSex(measures.sex || "male");
    setGoal(measures.goal || "lose");
    setActivity(measures.activityLevel || "moderate");
    setMealTimes(measures.mealTimes || defaultTimes);
  }, [measures]);

  const autoCalories = weight && height && age
    ? calcCalories(+weight, +height, +age, sex, goal, activity)
    : 0;

  const hasPaidPlan = selectedPlan && selectedPlan !== "free";

  const [savingMeasures, setSavingMeasures] = useState(false);

  const handleSave = async () => {
    if (!weight || !height || !age) return;
    const calories = calorieMode === "manual" && manualCalories ? +manualCalories : autoCalories;
    const w = +weight;

    // Calculate macro targets based on goal (centralized in macroSolver)
    const { protein: proteinG, carbs: carbsG, fat: fatG } = calcMacroTargets(w, goal, calories);

    const m: BodyMeasures = {
      weight: w, height: +height, age: +age, sex, goal,
      calories, mealTimes, activityLevel: activity,
      wantsTraining: false, preferences: [],
      macroTargets: { protein: proteinG, carbs: carbsG, fat: fatG },
    };

    setSavingMeasures(true);
    try {
      if (user) {
        await apiFetch("api-user-measures", undefined, {
          method: "POST",
          body: m,
        });
      } else {
        savePendingMeasures(m);
      }
      setMeasures(m);
      navigate("/diet-builder");
    } catch (e: any) {
      toast.error(e.message || "Erro ao salvar dados");
    } finally {
      setSavingMeasures(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Olá{user?.name ? `, ${user.name}` : ""}! 👋</h1>
        <p className="text-muted-foreground">Vamos configurar sua dieta personalizada</p>
      </div>

      {/* Medidas Corporais */}
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border space-y-5">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary" /> Medidas Corporais
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Peso (kg)</Label>
            <div className="relative">
              <Scale className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="number" placeholder="70" value={weight} onChange={e => setWeight(e.target.value)} className="pl-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Altura (cm)</Label>
            <div className="relative">
              <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="number" placeholder="175" value={height} onChange={e => setHeight(e.target.value)} className="pl-10" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Idade</Label>
            <div className="relative">
              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="number" placeholder="30" value={age} onChange={e => setAge(e.target.value)} className="pl-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Sexo</Label>
            <div className="flex gap-3">
              {(["male", "female"] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSex(s)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                    sex === s
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {s === "male" ? "Masculino" : "Feminino"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Objetivo */}
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border space-y-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" /> Objetivo
        </h2>
        <div className="grid grid-cols-3 gap-3">
          {goals.map(g => (
            <button
              key={g.value}
              type="button"
              onClick={() => setGoal(g.value)}
              className={`py-5 rounded-xl text-sm font-medium border-2 transition-all flex flex-col items-center gap-2 ${
                goal === g.value
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/30"
              }`}
            >
              <span className="text-2xl">{g.emoji}</span>
              <span className="text-foreground">{g.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Nível de Atividade */}
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border space-y-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" /> Nível de Atividade
        </h2>
        <div className="space-y-2">
          {activityLevels.map(a => (
            <button
              key={a.value}
              type="button"
              onClick={() => setActivity(a.value)}
              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border-2 transition-all text-left ${
                activity === a.value
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/30"
              }`}
            >
              <div>
                <p className="text-sm font-medium text-foreground">{a.label}</p>
                <p className="text-xs text-muted-foreground">{a.desc}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                activity === a.value ? "border-primary bg-primary" : "border-muted-foreground"
              }`}>
                {activity === a.value && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Calorias Diárias */}
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border space-y-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Flame className="w-5 h-5 text-primary" /> Calorias Diárias
        </h2>

        <div className="flex gap-3">
          {(["auto", "manual"] as const).map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => setCalorieMode(mode)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                calorieMode === mode
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {mode === "auto" ? "Automático" : "Manual"}
            </button>
          ))}
        </div>

        {calorieMode === "auto" ? (
          <div className="bg-secondary rounded-xl p-5 text-center space-y-2">
            <p className="text-sm text-muted-foreground">Calorias recomendadas</p>
            <p className="text-3xl font-bold text-primary tracking-tight">
              {autoCalories > 0 ? `${autoCalories} kcal` : "- - -"}
            </p>
            <p className="text-xs text-muted-foreground">Baseado nas suas medidas e objetivo</p>
            {autoCalories > 0 && weight && (
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border/50">
                 {(() => {
                   const w = +weight;
                   const { protein: prot, carbs: carbG, fat: fatG } = calcMacroTargets(w, goal, autoCalories);
                  return (
                    <>
                      <div className="text-center">
                        <p className="text-sm font-bold text-primary">{prot}g</p>
                        <p className="text-[10px] text-muted-foreground">Proteínas</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-bold text-accent">{carbG}g</p>
                        <p className="text-[10px] text-muted-foreground">Carboidratos</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-bold text-warning">{fatG}g</p>
                        <p className="text-[10px] text-muted-foreground">Gorduras</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Calorias por dia (kcal)</Label>
            <Input type="number" placeholder="2000" value={manualCalories} onChange={e => setManualCalories(e.target.value)} />
          </div>
        )}
      </div>

      {/* Meal times */}
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border space-y-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" /> Horários das Refeições
        </h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {["Café", "L. Manhã", "Almoço", "L. Tarde", "Jantar"].map((label, i) => (
            <div key={label} className="space-y-1.5">
              <span className="text-xs text-muted-foreground font-medium">{label}</span>
              <div className="relative">
                <Input
                  type="time"
                  value={mealTimes[i] || ""}
                  onChange={e => {
                    const t = [...mealTimes];
                    t[i] = e.target.value;
                    setMealTimes(t);
                  }}
                  className="h-12 text-base text-center bg-muted/40 border-border rounded-xl focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={handleSave} disabled={savingMeasures} className="w-full gradient-primary text-primary-foreground hover:opacity-90 transition-opacity" size="lg">
        {savingMeasures ? "Salvando..." : "Continuar para montar dieta →"}
      </Button>
    </motion.div>
  );
};

export default Dashboard;
